import React, {useContext, useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import type {ButtonColumn, DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {LocalizedText} from '@igp/shared/components/i18n';
import {ModalContext} from '@igp/shared/components/modal';
import {formatTimestamp} from '@igp/shared/utils';
import stringify from 'json-stringify-pretty-compact';

import type {KeycloakLogDataGridColumn, KeycloakLogViewModel} from '@features/keycloak-log/types';

import {operationTypeLocalized, resourceTypeLocalized} from '../constants';

const localized = defineMessages({
    date: {
        id: 'KeycloakLogDataGrid_date',
        defaultMessage: 'Date',
    },
    operator: {
        id: 'KeycloakLogDataGrid_operator',
        defaultMessage: 'Operator',
    },
    action: {
        id: 'KeycloakLogDataGrid_action',
        defaultMessage: 'Action',
    },
    targetUser: {
        id: 'KeycloakLogDataGrid_targetUser',
        defaultMessage: 'Target User',
    },
    details: {
        id: 'KeycloakLogDataGrid_details',
        defaultMessage: 'Details',
    },
    detailsTitle: {
        id: 'KeycloakLogDataGrid_detailsTitle',
        defaultMessage: 'Log Details',
    },
});

function formatDetails(details?: string): string {
    if (!details?.trim()) {
        return '';
    }

    try {
        return stringify(JSON.parse(details), {maxLength: 80});
    } catch {
        return details;
    }
}

type UseColumnsProps = {
    columns: KeycloakLogDataGridColumn[];
    onOpenDetails?: (row: KeycloakLogViewModel) => void;
};

function useColumns({columns, onOpenDetails}: UseColumnsProps): DataGridColumn<KeycloakLogViewModel>[] {
    const {formatMessage} = useIntl();
    const {openModal} = useContext(ModalContext);

    const defaultColumn: Partial<DataGridColumn<KeycloakLogViewModel>> = {
        width: 250,
    };

    const date: TextColumn<KeycloakLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'dateSeconds',
        title: localized.date,
        sortable: false,
        valueGetter: vm => (vm.dateSeconds ? formatTimestamp({seconds: vm.dateSeconds}, 'date-time-with-seconds') : ''),
    };

    const operator: TextColumn<KeycloakLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'operator',
        title: localized.operator,
        valueGetter: vm => vm.operatorUuid ?? '',
    };

    const action: TextColumn<KeycloakLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'action',
        title: localized.action,
        valueGetter: vm => {
            const resource = formatMessage(resourceTypeLocalized[vm.resourceType]);
            const operation = formatMessage(operationTypeLocalized[vm.operationType]);

            return [resource, operation].filter(Boolean).join(' ');
        },
    };

    const targetUser: TextColumn<KeycloakLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'targetUser',
        title: localized.targetUser,
        valueGetter: vm => vm.targetUserUuid ?? '',
    };

    const details: ButtonColumn<KeycloakLogViewModel> = {
        ...defaultColumn,
        width: 100,
        type: 'button',
        field: 'details',
        title: localized.details,
        valueGetter: vm => ({
            title: formatMessage(localized.details),
            disabled: !vm.details?.trim(),
            onClick: () => {
                if (onOpenDetails) {
                    onOpenDetails(vm);
                    return;
                }

                openModal({
                    title: <LocalizedText label={localized.detailsTitle} />,
                    body: <pre>{formatDetails(vm.details)}</pre>,
                });
            },
        }),
    };

    const config: Record<KeycloakLogDataGridColumn, DataGridColumn<KeycloakLogViewModel>> = {
        date,
        operator,
        action,
        targetUser,
        details,
    };

    return useMemo(() => columns.map(column => config[column]), [columns.join(',')]);
}

type KeycloakLogDataGridProps = Omit<DataGridServerProps<KeycloakLogViewModel, KeycloakLogDataGridColumn>, 'columns'> & {
    columns: KeycloakLogDataGridColumn[];
    onOpenDetails?: (row: KeycloakLogViewModel) => void;
};

export function KeycloakLogDataGrid({columns, onOpenDetails, ...props}: KeycloakLogDataGridProps) {
    const configuredColumns = useColumns({columns, onOpenDetails});

    return <DataGridServer {...props} columns={configuredColumns} />;
}
